// src/hooks/useWebSocket.js
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
    fetchMatches,
    fetchMatchesDonneurOrdre,
    fetchMessages,
    fetchMessagesDonneurOrdre,
    selectMatch
} from "../redux/actions/matchActions";

import {
    fetchLikesReceived,
    fetchLikesReceivedDonneurOrdre,
    fetchLikesSent,
    fetchLikesSentDonneurOrdre
} from "../redux/actions/likeActions";

import { setUserOnlineStatus } from "../redux/actions/onlineActions";
import { setUserTyping } from "../redux/actions/typingActions";

import {
    incomingCall,
    endCall,
    setCallOffer,
    clearCallOffer
} from "../redux/actions/callActions";

import { fetchNotifications } from "../redux/actions/notificationActions";
import { fetchPropositionPending } from "../redux/actions/propositionActions";
import { fetchUsersByRole } from "../redux/actions/userActions";
import { fetchProjetsClients } from "../redux/actions/projetClientActions";
import { fetchProfilPrestataire } from "../redux/actions/profilActions";
import { fetchPendingInterventions, fetchPlannedInterventions } from "../redux/actions/interventionActions";

export default function useWebSocket(wsRef) {
    const { user, token } = useSelector(state => state.auth);
    const { selectedMatch } = useSelector(state => state.match)
    const dispatch = useDispatch();

    const reconnectTimeout = useRef(null);
    const userRef = useRef(user);
    useEffect(() => { userRef.current = user }, [user]);

    // ------------------------------------------------------------
    // 1️⃣ CRÉATION DU WEBSOCKET (une seule fois)
    // ------------------------------------------------------------
    useEffect(() => {
        const connect = () => {
            if (wsRef.current) return; // <-- empêche les reconstructions multiples

            wsRef.current = new WebSocket(process.env.REACT_APP_WS_URL);

            wsRef.current.onopen = () => {
                // Identify à l'ouverture (guest ou connecté)
                const isConnected = Boolean(userRef.current);

                wsRef.current.send(JSON.stringify({
                    action: "identify",
                    env: process.env.REACT_APP_ENV,
                    userId: isConnected ? userRef.current.idUser : null,
                    role: isConnected ? userRef.current.rolesUser[0].roleUser : "guest",
                    connected: isConnected,
                }));

                if (isConnected) {
                    dispatch(setUserOnlineStatus(userRef.current.idUser, true));
                }
            };

            wsRef.current.onmessage = (event) => {
                try {
                    const msg = JSON.parse(event.data);
                    const { event: eventType, data } = msg;

                    // Événement global, même pour les visiteurs
                    if (eventType === "reload_required") {
                        window.location.reload(true);
                        return;
                    }

                    // Si pas connecté → ignore tous les autres événements
                    if (!userRef.current) return;

                    // Rafraîchissement notifications
                    dispatch(fetchNotifications(token));

                    switch (eventType) {

                        // 📞 Gestion appels
                        case "call_request":
                            dispatch(incomingCall({
                                fromUserId: data.fromUserId,
                                toUserId: data.toUserId,
                                matchId: data.matchId
                            }));
                            break;

                        case "call_offer":
                            dispatch(setCallOffer(data.offer));
                            break;

                        case "call_answer":
                        case "call_ice_candidate":
                            break;

                        case "call_hangup":
                            dispatch(endCall());
                            dispatch(clearCallOffer());
                            break;

                        // 👤 Statut utilisateur
                        case "user_status":
                            dispatch(setUserOnlineStatus(data.userId, data.online));
                            break;

                        // ✍️ Indicateur de frappe
                        case "typing":
                            dispatch(setUserTyping(data.matchId, data.fromUserId, data.isTyping));
                            break;

                        // 💬 Messages
                        case "new_message":
                            dispatch(fetchMessages(token, data.matchId));
                            break;

                        case "new_message_proposal_accepted":
                            dispatch(fetchMessages(token, data.proposition.match.id));
                            break;

                        case "new_message_donneur_ordre":
                            dispatch(fetchMessagesDonneurOrdre(token, data.matchId));
                            break;

                        // 👍 Likes
                        case "new_like_receive":
                            dispatch(fetchLikesReceived(token));
                            break;

                        case "new_like_donneur_ordre_received":
                            dispatch(fetchLikesReceivedDonneurOrdre(token));
                            break;

                        // 🤝 Matchs
                        case "new_match":
                            dispatch(fetchMatches(token));
                            dispatch(fetchLikesReceived(token));
                            dispatch(fetchLikesSent(token));
                            break;

                        case "new_match_donneur_ordre":
                            dispatch(fetchMatchesDonneurOrdre(token));
                            dispatch(fetchLikesReceivedDonneurOrdre(token));
                            dispatch(fetchLikesSentDonneurOrdre(token));
                            break;
                        case "project_canceled":
                            dispatch(fetchMatches(token));
                            if(user.rolesUser[0].roleUser === "client"){
                                dispatch(fetchProjetsClients(token));
                            }
                            break;

                        case "new_inscription_prestataire":
                            dispatch(fetchUsersByRole(token,"prestataire"));
                            break;
                        
                        case "new_inscription_client":
                            dispatch(fetchUsersByRole(token,"client"));
                            break;

                        case "new_inscription_donneur_ordre":
                            dispatch(fetchUsersByRole(token,"donneur_ordre"));
                            break;
                            
                        case "new_projet_client":
                            dispatch(fetchProjetsClients(token));

                        // 📄 Proposition commerciale
                        case "new_proposition":
                            dispatch(fetchPropositionPending(token, data.idProjet, data.idPrestataire));
                            break;
                        case "proposition_accepted":
                            if(selectedMatch.id === data.idMatch){
                                dispatch(selectMatch(null));
                                dispatch(fetchMatches(token));
                            }
                            break;
                        case "new_pending_intervention":
                            dispatch(fetchPendingInterventions(token));
                            break;
                        case "new_accepted_intervention":
                            dispatch(fetchPendingInterventions(token));
                            dispatch(fetchPlannedInterventions(token));
                            break;
                        case "new_finish_intervention":
                            dispatch(fetchPlannedInterventions(token));
                            break;
                        case "profil_presta_verified":
                            dispatch(fetchProfilPrestataire(token));
                            break;
                        case "new_prestataire_verif":
                            break;
                        default:
                            if (eventType !== "call_accept")
                                console.warn("WS EVENT INCONNU :", eventType);
                    }
                } catch (err) {
                    console.error("Erreur parse WS :", err);
                }
            };

            wsRef.current.onerror = () => {
                wsRef.current?.close();
            };

            wsRef.current.onclose = () => {
                wsRef.current = null;
                reconnectTimeout.current = setTimeout(connect, 3000);
            };
        };

        connect();

        return () => {
            if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
        };
    }, [dispatch]); // <-- PAS de dépendance user/token ici !


    // ------------------------------------------------------------
    // 2️⃣ RÉ-IDENTIFICATION AUTOMATIQUE (guest → connecté)
    // ------------------------------------------------------------
    useEffect(() => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

        wsRef.current.send(JSON.stringify({
            action: "identify",
            env: process.env.REACT_APP_ENV,
            userId: user ? user.idUser : null,
            role: user ? user.rolesUser[0].roleUser : "guest",
            connected: !!user,
        }));
    }, [user]);



    // ------------------------------------------------------------
    // 3️⃣ MÉTHODES D’ENVOI
    // ------------------------------------------------------------
    const sendTypingStatus = (matchId, toUserId, isTyping = true) => {
        if (!wsRef.current || !userRef.current) return;

        wsRef.current.send(JSON.stringify({
            action: "typing",
            matchId,
            fromUserId: userRef.current.idUser,
            toUserId,
            isTyping
        }));
    };

    const sendCallRequest = (matchId, fromUserId, toUserId) => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
        wsRef.current.send(JSON.stringify({ action: "call_request", matchId, fromUserId, toUserId }));
    };

    const sendCallAccept = (matchId, fromUserId, toUserId) => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
        wsRef.current.send(JSON.stringify({ action: "call_accept", matchId, fromUserId, toUserId }));
    };

    const hangupCall = (matchId, fromUserId, toUserId) => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
        wsRef.current.send(JSON.stringify({ action: "call_hangup", matchId, fromUserId, toUserId }));
    };

    const addCallListener = (callback) => {
        if (!wsRef.current) return;

        const handler = (event) => {
            try {
                const msg = JSON.parse(event.data);
                if (["call_request", "call_offer", "call_answer", "call_ice_candidate", "call_hangup"].includes(msg.event)) {
                    callback(msg.event, msg.data);
                }
            } catch { }
        };

        wsRef.current.addEventListener("message", handler);

        return () => wsRef.current?.removeEventListener("message", handler);
    };

    return {
        addCallListener,
        sendCallRequest,
        sendCallAccept,
        hangupCall,
        sendTypingStatus
    };
}
